import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  LoginWrapper,
  FormContainer,
  Form,
  Title,
  Button,
  LeftContainer,
  LoginLogo,
  ResetPasswordButton,
} from "../Login/Login.styles";
import AppInput from "@components/AppInput";
import LoginLogoSVG from "@assets/img/login-logo.svg";
import EmailIcon from "@assets/img/email-icon.svg";
import EmailCheck from "@assets/img/check-rounded.svg";
import { useNavigate } from "react-router-dom";
import { theme } from "@src/theme";
import { useAppDispatch } from "@src/redux/hooks";
import { sendEmailToResetPasswordThunk } from "@src/redux/thunks/authThunks";
import { useAuthState } from "@src/hooks/useAuth";
import LoginLeftContainer from "../Login/LoginLeftContainer";
import Spinner from "@src/components/Spinner";
import {
  clearError,
  resetPasswordEmailSent,
} from "@src/redux/slices/authSlice";

interface SendEmailToResetPassword {
  email: string;
}

const ResetPassword: React.FC = () => {
  const {
    isAuthenticated,
    resetPasswordEmailSent: resetPasswordEmailSentFlag,
    errorMessage,
    loading: loadingLogin,
  } = useAuthState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SendEmailToResetPassword>({
    resolver: yupResolver(
      Yup.object().shape({
        email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),
      })
    ),
  });

  const onSubmitSendEmail = (data: SendEmailToResetPassword): void => {
    dispatch(sendEmailToResetPasswordThunk({ email: data.email }));
  };

  const goToLogin = (): void => {
    navigate("/login");
  };

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(resetPasswordEmailSent(false));
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && !loadingLogin && !errorMessage) {
      navigate("/");
    }
  }, [isAuthenticated, loadingLogin, errorMessage]);

  return (
    <LoginWrapper>
      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmitSendEmail)}>
          <LoginLogo src={LoginLogoSVG} />
          <Title>RESET PASSWORD</Title>
          <div style={{ minHeight: 50 }}>
            {loadingLogin && <Spinner small />}
            {errorMessage && (
              <p style={{ color: theme.colors.red }}>{errorMessage}</p>
            )}
            {resetPasswordEmailSentFlag && (
              <p style={{ color: theme.colors.green }}>
                Email sent, please check your inbox
              </p>
            )}
          </div>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <AppInput
                label="PLEASE ENTER YOUR EMAIL"
                control={control}
                errors={errors}
                placeholder="Enter your email"
                {...field}
                icon={<img src={EmailIcon} alt="Email" />}
                iconRight={
                  <span
                    style={{
                      userSelect: "none",
                      opacity: errors.email ? 0.4 : 1,
                    }}
                  >
                    <img src={EmailCheck} alt="Email Check" />
                  </span>
                }
              />
            )}
          />

          <ResetPasswordButton type="button" onClick={goToLogin}>
            BACK TO LOGIN
          </ResetPasswordButton>
          <Button type="submit">SEND EMAIL</Button>
        </Form>
      </FormContainer>
      <LeftContainer>
        <LoginLeftContainer />
      </LeftContainer>
    </LoginWrapper>
  );
};

export default ResetPassword;
